/**
 * CheckoutAgreement - the agreement box under the pay button.
 *
 * "I agree to the Terms & Conditions and Privacy Policy" must be ticked before
 * the pay button works. The exact wording the fan saw, the terms version and
 * the time are sent with the payment (buildAgreement) and stored as dispute
 * evidence. The links open in a new tab so the checkout is not lost.
 */

import { LEGAL, LEGAL_STYLE, TERMS_VERSION } from '@/config/legalConfig'

export const AGREEMENT_LABEL = 'I agree to the Terms & Conditions and Privacy Policy.'

export function statementLine(): string {
  return `It will appear on your card statement as ${LEGAL.statementName}.`
}

/** What is sent with the payment and recorded on it. */
export function buildAgreement(chargeLine: string) {
  return {
    accepted: true,
    version: TERMS_VERSION,
    clientTime: new Date().toISOString(),
    shown: `${AGREEMENT_LABEL} ${chargeLine} ${statementLine()}`,
  }
}

export const AGREEMENT_REQUIRED = 'Please tick the box to agree to the Terms & Conditions and Privacy Policy.'

interface CheckoutAgreementProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** e.g. "Secure payment via Stripe. Your card will be charged once for one year of access. No auto renewal." */
  chargeLine: string
}

export function CheckoutAgreement({ checked, onChange, chargeLine }: CheckoutAgreementProps) {
  const link = LEGAL_STYLE.link
  return (
    <div className="space-y-3" data-testid="checkout-agreement">
      <label htmlFor="terms-agree" className="flex items-start gap-3 cursor-pointer select-none">
        <input
          id="terms-agree"
          data-testid="terms-agree"
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className={`mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded ${LEGAL_STYLE.checkbox}`}
        />
        <span className="text-sm text-gray-700 leading-snug">
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className={link}>Terms &amp; Conditions</a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className={link}>Privacy Policy</a>.
        </span>
      </label>

      <p className="flex items-start gap-2 text-xs leading-relaxed text-gray-500">
        <svg className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path strokeLinecap="round" d="M8 11V7a4 4 0 118 0v4" />
        </svg>
        <span data-testid="checkout-charge-line">{chargeLine} {statementLine()}</span>
      </p>

      <nav className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-t border-gray-200 pt-3 text-xs text-gray-500" aria-label="Policies">
        <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms &amp; Conditions</a>
        <span aria-hidden="true">|</span>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Policy</a>
        <span aria-hidden="true">|</span>
        <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="hover:underline">Refund Policy</a>
        <span aria-hidden="true">|</span>
        <a href={`mailto:${LEGAL.supportEmail}`} className="hover:underline">Contact Us</a>
      </nav>
    </div>
  )
}

/** The "what you get" list in the summary box. */
export function OfferChecklist({ items }: { items?: string[] }) {
  const list = items || LEGAL.offer.checklist
  return (
    <ul className="mt-3 space-y-1.5 text-left inline-block" data-testid="offer-checklist">
      {list.map((item) => (
        <li key={item} className="flex items-center gap-2 text-sm text-gray-700">
          <svg className={`h-4 w-4 flex-shrink-0 ${LEGAL_STYLE.check}`} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {item}
        </li>
      ))}
    </ul>
  )
}
